import React, { useContext, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import logoLgt from '../assets/CourierLink-Footer-Logo.png'
import LogosTicker from './LogosTicker'
import CalQuote from './CalQuote'
import HeroAnimation from './HeroAnimation'
import compare from '../assets/compare.svg'
import booking from '../assets/booking.svg'
import delivery from '../assets/delivery.svg'
import { QuoteContext } from '../assets/context/QuoteContext'
import url from './url'

const parseFaqs = (body = '') => body
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block.startsWith('## '))
    .map((block) => {
        const [question, ...answer] = block.split('\n');
        return { question: question.slice(3).trim(), answer: answer.join('\n').trim() };
    });

function Home() {
    const { resetBookingFlow } = useContext(QuoteContext);
    const [faqs, setFaqs] = useState([]);

    useEffect(() => {
        let active = true;
        fetch(`${url}/api/site-content/faq`)
            .then((response) => response.ok ? response.json() : Promise.reject(new Error('Unable to load FAQs')))
            .then((page) => {
                if (active) setFaqs(parseFaqs(page.body));
            })
            .catch((error) => console.error('Homepage FAQ error:', error));
        return () => { active = false; };
    }, []);

    return (
        <main className="home-page">
            <HeroAnimation />
            <section className="hero-section">
                <div className="container hero-layout">
                    <div className="hero-copy">
                        <div className="eyebrow"><span className="eyebrow-dot"></span> Smarter shipping starts here</div>
                        <h1>Move what<br /><em>matters.</em> <span>with confidence.</span></h1>
                        <p className="hero-description">Compare trusted carriers, book in minutes, and follow every delivery from pickup to arrival — all in one calm, simple workspace.</p>
                        <div className="hero-actions justify-center items-center">
                            <NavLink to="/CalQuote" className="btn btn-rnd" onClick={resetBookingFlow}>Get a quote <span aria-hidden="true">↗</span></NavLink>
                            <a href="/#how-it-works" className="text-link">See how it works <span aria-hidden="true">↓</span></a>
                        </div>
                    </div>
                </div>
                <div className="hero-scroll-note" aria-hidden="true"><span>Scroll to explore</span><i></i></div>
            </section>

            <div className="stats-strip container">
                <div><strong>30+</strong><span>carrier options</span></div>
                <div><strong>1 place</strong><span>to manage every shipment</span></div>
                <div><strong>24/7</strong><span>shipment visibility</span></div>
            </div>
            <div className="carrier-strip">
                <div className="container carrier-strip-inner"><span>Trusted by the world's best carriers</span><LogosTicker /></div>
            </div>

            <section id="how-it-works" className="container how-it-works">
                <div className="section-heading">
                    <div><span className="section-kicker">THE SIMPLE WAY TO SHIP</span><h2>From doorstep<br /><em>to destination.</em></h2></div>
                    <p>Everything you need to make a delivery happen, without the usual back-and-forth.</p>
                </div>
                <div className="workflow-grid">
                    <div className="workflow-card">
                        <div className="workflow-icon"><img src={compare} alt="" /></div>
                        <span className="step-number">01</span>
                        <h3>Compare</h3>
                        <p>See clear, competitive quotes from reliable carriers in seconds.</p>
                        <span className="card-arrow">↗</span>
                    </div>
                    <div className="workflow-card workflow-card-dark">
                        <div className="workflow-icon"><img src={booking} alt="" /></div>
                        <span className="step-number">02</span>
                        <h3>Book</h3>
                        <p>Choose the service that fits and confirm your shipment with ease.</p>
                        <span className="card-arrow">↗</span>
                    </div>
                    <div className="workflow-card workflow-card-lime">
                        <div className="workflow-icon"><img src={delivery} alt="" /></div>
                        <span className="step-number">03</span>
                        <h3>Deliver</h3>
                        <p>Stay in the loop with tracking updates all the way to arrival.</p>
                        <span className="card-arrow">↗</span>
                    </div>
                </div>
            </section>

            <section id="quote-form" className="home-cal-quote-container">
                <CalQuote />
            </section>

            <section className="container promises-section">
                <div className="promise-intro"><span className="section-kicker">THE COURIERLINK PROMISE</span><h2>Less waiting.<br /><em>More moving.</em></h2></div>
                <div className="promise-list">
                    <div><span>01</span><div><h3>On-time delivery</h3><p>Reliable services with clear delivery estimates from the start.</p></div></div>
                    <div><span>02</span><div><h3>Secure by design</h3><p>Protected payments and shipment details you can trust.</p></div></div>
                    <div><span>03</span><div><h3>Human support</h3><p>Real help when you need it, from quote to delivery.</p></div></div>
                </div>
            </section>

            <section id="faqs" className="home-faq-section" aria-labelledby="home-faq-title">
                <div className="container home-faq-layout">
                    <div className="home-faq-intro">
                        <span className="section-kicker">GOOD TO KNOW</span>
                        <h2 id="home-faq-title">Frequently asked<br /><em>questions.</em></h2>
                        <p>Quick answers for planning, booking, and tracking your next delivery.</p>
                    </div>
                    <div className="home-faq-list">
                        {faqs.map((faq, index) => (
                            <details key={faq.question} className="faq-item" open={index === 0}>
                                <summary>{faq.question}<span aria-hidden="true">+</span></summary>
                                <div><p>{faq.answer}</p></div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <section className="booknow-cta">
                <div className="container">
                    <div className="cta-copy"><span className="section-kicker">READY WHEN YOU ARE</span><h2>Make your next<br /><em>move a good one.</em></h2><p>Get a clear quote in under a minute.</p><NavLink to="/CalQuote" className="btn btn-light" onClick={resetBookingFlow}>Start a shipment <span aria-hidden="true">↗</span></NavLink></div>
                </div>
            </section>

            <footer className="site-footer">
                <div className="container">
                    <div className="footer-top">
                        <div><img className="footer-logo" src={logoLgt} alt="CourierLink" /><p>Compare. Book. Delivered.</p></div>
                        <div className="footer-links"><div><span>Explore</span><a href="/#how-it-works">How it works</a><NavLink to="/Tracking">Track a shipment</NavLink><NavLink to="/faq">FAQs</NavLink></div><div><span>Legal</span><NavLink to="/terms">Terms &amp; conditions</NavLink><NavLink to="/privacy">Privacy policy</NavLink><a href="mailto:support@courierlink.com.au">Contact us</a></div></div>
                    </div>
                    <div className="footer-bottom"><span>© 2026 CourierLink</span><span>Built for better deliveries.</span></div>
                </div>
            </footer>
        </main>
    )
}

export default Home